import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  CheckCircle2,
  ShieldAlert,
  Bug,
  LogOut,
  Users,
  LayoutDashboard,
  Shield,
  Zap,
  Bell,
  Sliders,
  Sparkles,
} from "lucide-react";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { UserManagementView } from "./components/UserManagementView";
import { WidgetDemoView } from "./components/WidgetDemoView";
import { IssueFilterBar } from "./components/IssueFilterBar";
import { IssueListView } from "./components/IssueListView";
import { IssueKanbanView } from "./components/IssueKanbanView";
import { CreateIssueModal } from "./components/CreateIssueModal";
import { IssueDetailModal } from "./components/IssueDetailModal";
import { NotificationPreferencesModal } from "./components/NotificationPreferencesModal";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserProfile, Issue, UserRole, IssueStatus } from "./types";
import {
  subscribeToIssues,
  bulkUpdateIssueStatus,
  bulkAssignIssues,
  bulkDeleteIssues,
} from "./services/issueService";
import {
  fetchUsers,
  subscribeToUsers,
  triggerHrSync,
  listActiveHrStaff,
  importSelectedHrStaff,
} from "./services/auth";
import {
  getUnreadResponseCount,
  getTicketNotifications,
  markTicketAsRead,
  markAllTicketsAsRead,
  formatRelativeTime,
} from "./utils/notificationTracker";
import { getAppPillClasses } from "./utils/appColors";

// Fallback demo user for test environments
export const demoUser: UserProfile = {
  uid: "dev-admin-1",
  email: "mark@mlconnections.com",
  displayName: "Mark L.",
  role: "it_admin",
  status: "active",
  allowedApps: ["operations-hub", "mlc-hr-tools", "viq-autostart"],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

interface HeaderProps {
  currentUser: UserProfile | null;
  activeTab: "dashboard" | "users" | "widget";
  onTabChange: (tab: "dashboard" | "users" | "widget") => void;
  onOpenPreferences?: () => void;
  issues?: Issue[];
  onSelectIssue?: (issue: Issue) => void;
}

const AppHeader: React.FC<HeaderProps> = ({
  currentUser,
  activeTab,
  onTabChange,
  onOpenPreferences,
  issues = [],
  onSelectIssue,
}) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [readTick, setReadTick] = useState(0);

  // Sync state when read receipts change
  useEffect(() => {
    const handleUpdate = () => {
      setReadTick((prev) => prev + 1);
    };
    window.addEventListener("mlc-ticket-read-updated", handleUpdate);
    return () => {
      window.removeEventListener("mlc-ticket-read-updated", handleUpdate);
    };
  }, []);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  const unreadCount = useMemo(() => {
    return getUnreadResponseCount(issues, currentUser);
  }, [issues, currentUser, readTick]);

  const notifications = useMemo(() => {
    return getTicketNotifications(issues, currentUser);
  }, [issues, currentUser, readTick]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (err: unknown) {
      console.error("Sign out error:", err);
    }
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200/80 p-1 flex items-center justify-center shadow-xs">
              <img
                src="/mlc-logo.png"
                alt="MLC"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
                MLC Ticketing{" "}
                <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold border border-teal-200">
                  Hub
                </span>
              </h1>
              <p className="text-xs text-slate-500">
                Centralized Issue & Feedback Operations
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="hidden md:flex items-center gap-1 border-l border-slate-200 pl-6">
            <button
              type="button"
              onClick={() => onTabChange("dashboard")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeTab === "dashboard"
                  ? "bg-teal-50 text-teal-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>All Tickets</span>
            </button>

            {(currentUser?.role === "it_admin" ||
              currentUser?.role === "dept_admin") && (
              <button
                type="button"
                onClick={() => onTabChange("users")}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  activeTab === "users"
                    ? "bg-teal-50 text-teal-700"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Users</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => onTabChange("widget")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeTab === "widget"
                  ? "bg-teal-50 text-teal-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Widget</span>
            </button>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            {currentUser?.photoURL ? (
              <img
                src={currentUser.photoURL}
                alt={currentUser.displayName}
                className="w-8 h-8 rounded-full object-cover border border-teal-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-xs">
                {(currentUser?.displayName || "ML").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-slate-800">
                {currentUser?.displayName || "Mark L."}
              </p>
              <p className="text-[10px] text-slate-500">
                {currentUser?.role === "it_admin"
                  ? "IT Administrator"
                  : currentUser?.role === "dept_admin"
                    ? "Department Lead"
                    : "End User"}
              </p>
            </div>

            {/* Notification Bell Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                title="Notifications"
                aria-label="Notifications"
                aria-expanded={isDropdownOpen}
                className="relative p-1.5 text-slate-500 hover:text-teal-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span
                    data-testid="notification-bell-badge"
                    className="absolute -top-1 -right-1 flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-rose-600 text-[10px] font-bold text-white shadow-xs"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {isDropdownOpen && (
                <div
                  data-testid="notifications-dropdown"
                  className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                >
                  {/* Dropdown Header */}
                  <div className="p-3 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-800 text-xs tracking-tight">
                        Notifications
                      </h3>
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold">
                          {unreadCount} unread
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {unreadCount > 0 && currentUser?.uid && (
                        <button
                          type="button"
                          data-testid="mark-all-read-btn"
                          onClick={() => {
                            markAllTicketsAsRead(issues, currentUser.uid);
                          }}
                          className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer px-1 py-0.5"
                        >
                          Mark all as read
                        </button>
                      )}
                      {onOpenPreferences && (
                        <button
                          type="button"
                          data-testid="notification-settings-btn"
                          title="Notification Settings"
                          aria-label="Notification Settings"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            onOpenPreferences();
                          }}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold text-slate-600 hover:text-teal-700 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 transition-colors cursor-pointer shadow-2xs"
                        >
                          <Sliders className="w-3.5 h-3.5 text-teal-600" />
                          <span>Settings</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Dropdown Notifications List */}
                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-400 text-xs">
                        <CheckCircle2 className="w-6 h-6 mx-auto mb-1.5 text-emerald-500" />
                        <p className="font-medium text-slate-600">
                          All caught up!
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          No recent responses on your tickets.
                        </p>
                      </div>
                    ) : (
                      notifications.map((item) => (
                        <div
                          key={item.issueId}
                          data-testid={`notification-item-${item.issueId}`}
                          onClick={() => {
                            if (currentUser?.uid) {
                              markTicketAsRead(item.issueId, currentUser.uid);
                            }
                            setIsDropdownOpen(false);
                            const issue = issues.find(
                              (i) => i.id === item.issueId,
                            );
                            if (issue && onSelectIssue) {
                              onSelectIssue(issue);
                            }
                          }}
                          className={`p-3 transition-colors cursor-pointer hover:bg-slate-50 flex items-start gap-2.5 ${
                            item.isUnread ? "bg-rose-50/40" : "bg-white"
                          }`}
                        >
                          <div className="pt-0.5 shrink-0">
                            {item.isUnread ? (
                              <span className="w-2 h-2 rounded-full bg-rose-500 block animate-pulse" />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-transparent block" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center justify-between gap-1">
                              <span
                                className={`inline-block text-[10px] font-bold px-2 py-0.2 rounded-full border shadow-2xs ${getAppPillClasses(item.appName)}`}
                              >
                                {item.appName}
                              </span>
                              <span className="text-[10px] font-mono font-bold text-teal-700">
                                {item.issueId}
                              </span>
                            </div>
                            <p className="text-xs text-slate-800 leading-snug line-clamp-1">
                              <span className="font-semibold text-slate-900">
                                {item.authorName}
                              </span>{" "}
                              responded:
                            </p>
                            <p className="text-[11px] text-slate-500 line-clamp-2 italic bg-slate-50/80 p-1.5 rounded border border-slate-100">
                              &ldquo;{item.commentPreview}&rdquo;
                            </p>
                            <p className="text-[10px] text-slate-400">
                              {formatRelativeTime(item.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => void handleSignOut()}
              title="Sign Out"
              aria-label="Sign Out"
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export interface DashboardViewProps {
  issues?: Issue[];
  onIssuesChange?: (updater: Issue[] | ((prev: Issue[]) => Issue[])) => void;
  selectedNotificationIssue?: Issue | null;
  onClearNotificationIssue?: () => void;
  onCloseDetailModal?: () => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  issues: propIssues,
  onIssuesChange,
  selectedNotificationIssue,
  onClearNotificationIssue,
  onCloseDetailModal,
}) => {
  const { user } = useAuth();
  const currentUser = user || demoUser;

  // Real-time issues state
  const [internalIssues, setInternalIssues] = useState<Issue[]>([]);
  const issues = propIssues !== undefined ? propIssues : internalIssues;

  const setIssues = useCallback(
    (updater: Issue[] | ((prev: Issue[]) => Issue[])) => {
      if (onIssuesChange) {
        onIssuesChange(updater);
      } else {
        setInternalIssues(updater);
      }
    },
    [onIssuesChange],
  );

  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (selectedNotificationIssue) {
      setSelectedIssue(selectedNotificationIssue);
      onClearNotificationIssue?.();
    }
  }, [selectedNotificationIssue, onClearNotificationIssue]);

  // Filters and views state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedApp, setSelectedApp] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [selectedAssignment, setSelectedAssignment] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("mlc_filter_status") || "active";
    }
    return "active";
  });
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");

  const handleStatusFilterChange = useCallback((newStatus: string) => {
    setSelectedStatus(newStatus);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mlc_filter_status", newStatus);
    }
  }, []);

  // Real-time admin users state for ticket assignment
  const [adminUsers, setAdminUsers] = useState<UserProfile[]>([currentUser]);

  useEffect(() => {
    if (propIssues !== undefined) return;
    const unsub = subscribeToIssues(
      (loaded) => {
        setInternalIssues(loaded);
      },
      (err) => {
        console.error("Failed to load issues:", err);
      },
    );
    return () => unsub();
  }, [propIssues]);

  // Real-time admin users subscription for assignment dropdown
  useEffect(() => {
    const unsub = subscribeToUsers(
      (allUsers) => {
        const admins = allUsers.filter(
          (u) =>
            u.status === "active" &&
            (u.role === "it_admin" || u.role === "dept_admin"),
        );
        admins.sort((a, b) =>
          (a.displayName || a.email || "").localeCompare(
            b.displayName || b.email || "",
          ),
        );
        setAdminUsers(admins);
      },
      (err) => {
        console.error("Failed to load admin users for assignment:", err);
      },
    );
    return () => unsub();
  }, []);

  const handleIssueCreated = useCallback((newIssue: Issue) => {
    setIssues((prev) => {
      if (prev.some((i) => i.id === newIssue.id)) {
        return prev;
      }
      return [newIssue, ...prev];
    });
  }, []);

  const handleIssueUpdated = useCallback(
    (updates: Partial<Issue>) => {
      setIssues((prev) =>
        prev.map((i) =>
          i.id === selectedIssue?.id ? { ...i, ...updates } : i,
        ),
      );
      setSelectedIssue((prev) => (prev ? { ...prev, ...updates } : null));
    },
    [selectedIssue],
  );

  const handleBulkStatusChange = useCallback(
    async (issueIds: string[], newStatus: IssueStatus) => {
      const prevMap = new Map<string, IssueStatus>();
      issues.forEach((i) => prevMap.set(i.id, i.status));
      await bulkUpdateIssueStatus(issueIds, newStatus, currentUser, prevMap);
      setIssues((prev) =>
        prev.map((i) =>
          issueIds.includes(i.id) ? { ...i, status: newStatus } : i,
        ),
      );
    },
    [issues, currentUser],
  );

  const handleBulkAssign = useCallback(
    async (
      issueIds: string[],
      assignee: { uid: string; email: string; displayName: string } | null,
    ) => {
      const prevMap = new Map<string, string | null>();
      issues.forEach((i) => prevMap.set(i.id, i.assigneeEmail));
      await bulkAssignIssues(issueIds, assignee, currentUser, prevMap);
      setIssues((prev) =>
        prev.map((i) =>
          issueIds.includes(i.id)
            ? {
                ...i,
                assigneeUid: assignee?.uid ?? null,
                assigneeEmail: assignee?.email ?? null,
              }
            : i,
        ),
      );
    },
    [issues, currentUser],
  );

  const handleBulkDelete = useCallback(async (issueIds: string[]) => {
    await bulkDeleteIssues(issueIds);
    setIssues((prev) => prev.filter((i) => !issueIds.includes(i.id)));
  }, []);

  const handleDeleteIssue = useCallback(async (issueId: string) => {
    await bulkDeleteIssues([issueId]);
    setIssues((prev) => prev.filter((i) => i.id !== issueId));
    setSelectedIssue((prev) => (prev?.id === issueId ? null : prev));
  }, []);

  // Dynamically derive available apps from tickets pushed into the database
  const availableApps = useMemo(() => {
    const appMap = new Map<string, string>();
    for (const issue of issues) {
      if (issue.appName && issue.appName.trim()) {
        const id = issue.appId || issue.appName;
        if (!appMap.has(id)) {
          appMap.set(id, issue.appName);
        }
      }
    }
    const list = Array.from(appMap.entries()).map(([id, name]) => ({
      id,
      name,
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [issues]);

  // Compute live summary statistics on unique issues
  const stats = useMemo(() => {
    const uniqueMap = new Map<string, Issue>();
    for (const issue of issues) {
      if (issue.id && !uniqueMap.has(issue.id)) {
        uniqueMap.set(issue.id, issue);
      }
    }
    const uniqueIssues = Array.from(uniqueMap.values());

    const unassigned = uniqueIssues.filter(
      (i) => !i.assigneeUid && i.status !== "closed" && i.status !== "resolved",
    ).length;
    const inProgress = uniqueIssues.filter(
      (i) => i.status === "in_progress" || i.status === "in_review",
    ).length;
    const resolved = uniqueIssues.filter((i) => i.status === "resolved").length;
    const breaches = uniqueIssues.filter(
      (i) => i.sla?.status === "breached",
    ).length;

    return { unassigned, inProgress, resolved, breaches };
  }, [issues]);

  // Filter issues based on active controls and ensure uniqueness by id
  const filteredIssues = useMemo(() => {
    const seen = new Set<string>();
    return issues.filter((i) => {
      if (!i.id || seen.has(i.id)) return false;
      seen.add(i.id);

      if (
        selectedApp !== "all" &&
        i.appId !== selectedApp &&
        i.appName !== selectedApp &&
        !(selectedApp === "teppi-portal" && i.appId === "teppy-portal")
      )
        return false;
      if (selectedCategory !== "all") {
        if (
          selectedCategory === "feature_request" ||
          selectedCategory === "suggestion"
        ) {
          if (i.category !== "feature_request") return false;
        } else if (i.category !== selectedCategory) {
          return false;
        }
      }
      if (selectedPriority !== "all" && i.priority !== selectedPriority)
        return false;
      if (selectedStatus === "active") {
        if (i.status === "resolved" || i.status === "closed") return false;
      } else if (selectedStatus !== "all" && i.status !== selectedStatus) {
        return false;
      }
      if (selectedAssignment === "unassigned") {
        if (i.assigneeUid || i.assigneeEmail) return false;
      } else if (selectedAssignment === "assigned") {
        if (!i.assigneeUid && !i.assigneeEmail) return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesId = i.id.toLowerCase().includes(query);
        const matchesTitle = i.title.toLowerCase().includes(query);
        const matchesDesc = (i.description || "").toLowerCase().includes(query);
        const matchesReporter = (i.reporterName || "")
          .toLowerCase()
          .includes(query);
        if (!matchesId && !matchesTitle && !matchesDesc && !matchesReporter)
          return false;
      }
      return true;
    });
  }, [
    issues,
    selectedApp,
    selectedCategory,
    selectedPriority,
    selectedStatus,
    selectedAssignment,
    searchQuery,
  ]);

  return (
    <div className="space-y-6">
      {/* Top Title & Subtitle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            All Issues &amp; Tickets
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Cross-application issue tracking for Operations Hub, HR Tools, VIQ,
            and more.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-animate inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-600 text-white rounded-lg text-xs sm:text-sm font-semibold shadow-md shadow-teal-600/20 hover:bg-teal-700 cursor-pointer shrink-0"
        >
          <Sparkles className="w-4 h-4" />
          <span>Submit Issue</span>
        </button>
      </div>

      {/* Status Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          role="button"
          tabIndex={0}
          aria-label={`Filter by unassigned tickets. Currently ${
            selectedAssignment === "unassigned" ? "active" : "inactive"
          }`}
          onClick={() => {
            setSelectedAssignment((prev) =>
              prev === "unassigned" ? "all" : "unassigned",
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedAssignment((prev) =>
                prev === "unassigned" ? "all" : "unassigned",
              );
            }
          }}
          className={`glass rounded-xl p-5 cursor-pointer transition-all border ${
            selectedAssignment === "unassigned"
              ? "border-amber-400 bg-amber-50/70 shadow-xs ring-2 ring-amber-400/50"
              : "hover:border-amber-300 hover:shadow-2xs border-slate-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
              <span>Unassigned</span>
              {selectedAssignment === "unassigned" && (
                <span className="text-[10px] lowercase font-semibold px-1.5 py-0.2 rounded bg-amber-200 text-amber-900 font-mono">
                  filtered
                </span>
              )}
            </span>
            <ShieldAlert className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">
            {stats.unassigned}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            {stats.unassigned === 0
              ? "All tickets currently triaged"
              : `${stats.unassigned} awaiting triage · click to ${
                  selectedAssignment === "unassigned" ? "clear" : "filter"
                }`}
          </p>
        </div>

        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              In Progress
            </span>
            <Bug className="w-5 h-5 text-teal-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">
            {stats.inProgress}
          </p>
          <p className="text-xs text-slate-500 mt-1">Active investigations</p>
        </div>

        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Resolved
            </span>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">
            {stats.resolved}
          </p>
          <p className="text-xs text-emerald-600 mt-1">Resolved this sprint</p>
        </div>

        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              SLA Breaches
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                stats.breaches === 0
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-rose-100 text-rose-700"
              }`}
            >
              {stats.breaches === 0 ? "Healthy" : `${stats.breaches} Breached`}
            </span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">
            {stats.breaches}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {stats.breaches === 0
              ? "100% on-track SLA record"
              : "Action required"}
          </p>
        </div>
      </div>

      {/* Filter and View Controls Bar */}
      <IssueFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        availableApps={availableApps}
        selectedApp={selectedApp}
        onAppChange={setSelectedApp}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedPriority={selectedPriority}
        onPriorityChange={setSelectedPriority}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusFilterChange}
        selectedAssignment={selectedAssignment}
        onAssignmentChange={setSelectedAssignment}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* All Tickets: List or Kanban */}
      {viewMode === "list" ? (
        <IssueListView
          issues={filteredIssues}
          onSelectIssue={(issue) => setSelectedIssue(issue)}
          onCreateIssue={() => setIsCreateModalOpen(true)}
          currentUser={currentUser}
          adminUsers={adminUsers}
          onBulkStatusChange={handleBulkStatusChange}
          onBulkAssign={handleBulkAssign}
          onBulkDelete={handleBulkDelete}
          onDeleteIssue={handleDeleteIssue}
        />
      ) : (
        <IssueKanbanView
          issues={filteredIssues}
          onSelectIssue={(issue) => setSelectedIssue(issue)}
          onShowAllStatuses={() => handleStatusFilterChange("all")}
          isStatusFiltered={selectedStatus === "active"}
          currentUser={currentUser}
        />
      )}

      {/* Create Issue Modal */}
      <CreateIssueModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        currentUser={currentUser}
        initialAppId={selectedApp !== "all" ? selectedApp : undefined}
        onIssueCreated={handleIssueCreated}
      />

      {/* Issue Detail Modal (Ticket View) */}
      <IssueDetailModal
        issue={selectedIssue}
        isOpen={!!selectedIssue}
        onClose={() => {
          setSelectedIssue(null);
          onCloseDetailModal?.();
        }}
        currentUser={currentUser}
        adminUsers={adminUsers}
        onIssueUpdated={handleIssueUpdated}
      />
    </div>
  );
};

export const MainAppLayout: React.FC = () => {
  const { user, loading, refreshProfile, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketParam = searchParams.get("ticket") || searchParams.get("issue");
  const [handledTicketParam, setHandledTicketParam] = useState<string | null>(
    null,
  );

  const [activeTab, setActiveTab] = useState<"dashboard" | "users" | "widget">(
    "dashboard",
  );
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [selectedIssueFromHeader, setSelectedIssueFromHeader] =
    useState<Issue | null>(null);

  useEffect(() => {
    const unsub = subscribeToIssues(
      (loaded) => {
        setIssues(loaded);
      },
      (err) => {
        console.error("Failed to load issues:", err);
      },
    );
    return () => unsub();
  }, []);

  // Deep linking: detect ?ticket=... or ?issue=... and auto-open modal
  useEffect(() => {
    if (!ticketParam || issues.length === 0) return;
    const normalized = ticketParam.trim().toLowerCase();
    if (handledTicketParam === normalized) return;

    const matched = issues.find((i) => i.id.toLowerCase() === normalized);
    if (matched) {
      setHandledTicketParam(normalized);
      setActiveTab("dashboard");
      setSelectedIssueFromHeader(matched);
    }
  }, [ticketParam, issues, handledTicketParam]);

  const handleSelectIssueFromHeader = useCallback((issue: Issue) => {
    setActiveTab("dashboard");
    setSelectedIssueFromHeader(issue);
  }, []);

  const handleClearSelectedIssueFromHeader = useCallback(() => {
    setSelectedIssueFromHeader(null);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setSelectedIssueFromHeader(null);
    setSearchParams(
      (prev) => {
        if (!prev.has("ticket") && !prev.has("issue")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("ticket");
        next.delete("issue");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleTriggerHrSync = useCallback(async () => {
    return triggerHrSync(user?.email || demoUser.email);
  }, [user?.email]);

  const handleListActiveHrStaff = useCallback(async () => {
    return listActiveHrStaff(user?.email || demoUser.email);
  }, [user?.email]);

  const handleImportSelectedHrStaff = useCallback(
    async (selectedUserIds: string[], role?: UserRole) => {
      return importSelectedHrStaff(
        selectedUserIds,
        role,
        "active",
        user?.email || demoUser.email,
      );
    },
    [user?.email],
  );

  return (
    <ProtectedRoute
      user={user}
      loading={loading}
      onRefresh={refreshProfile}
      onSignOut={signOut}
    >
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <AppHeader
          currentUser={user}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onOpenPreferences={() => setIsPreferencesOpen(true)}
          issues={issues}
          onSelectIssue={handleSelectIssueFromHeader}
        />
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === "dashboard" ? (
            <DashboardView
              issues={issues}
              onIssuesChange={setIssues}
              selectedNotificationIssue={selectedIssueFromHeader}
              onClearNotificationIssue={handleClearSelectedIssueFromHeader}
              onCloseDetailModal={handleCloseDetailModal}
            />
          ) : activeTab === "users" ? (
            <UserManagementView
              currentUser={user || demoUser}
              onFetchUsers={fetchUsers}
              onTriggerHrSync={handleTriggerHrSync}
              onListActiveHrStaff={handleListActiveHrStaff}
              onImportSelectedHrStaff={handleImportSelectedHrStaff}
            />
          ) : (
            <WidgetDemoView currentUser={user || demoUser} />
          )}
        </main>

        {isPreferencesOpen && (user || demoUser) && (
          <NotificationPreferencesModal
            isOpen={isPreferencesOpen}
            currentUser={user || demoUser}
            onClose={() => setIsPreferencesOpen(false)}
            onSaved={async () => {
              await refreshProfile();
            }}
          />
        )}
      </div>
    </ProtectedRoute>
  );
};

export const LoginView: React.FC = () => {
  const { signIn, loading, error, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (user && user.status === "active") {
      const stateFrom = (
        location.state as { from?: { pathname?: string; search?: string } }
      )?.from;
      if (stateFrom) {
        navigate(`${stateFrom.pathname || "/"}${stateFrom.search || ""}`, {
          replace: true,
        });
        return;
      }
      const search = window.location.search;
      navigate(search ? `/${search}` : "/", { replace: true });
    }
  }, [user, navigate, location.state]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="glass max-w-sm w-full rounded-2xl p-8 shadow-xl text-center border border-slate-200">
        <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 p-2 flex items-center justify-center mx-auto shadow-sm mb-4">
          <img
            src="/mlc-logo.png"
            alt="MLC Logo"
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="mt-2 text-xl font-bold text-slate-900">MLC Ticketing</h2>
        <p className="mt-2 text-xs text-slate-600">
          Sign in with your @mlconnections.com Google Workspace account
        </p>

        {error && (
          <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg text-left">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={() => void signIn(false)}
          disabled={loading}
          className="btn-animate mt-6 inline-flex w-full justify-center items-center gap-2 rounded-lg bg-teal-600 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-teal-700 disabled:opacity-50"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Shield className="w-4 h-4" />
          )}
          <span>{loading ? "Signing In..." : "Sign in with Google"}</span>
        </button>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => void signIn(true)}
            className="text-xs text-slate-500 hover:text-teal-700 underline transition-colors"
          >
            Popup blocked or stuck? Sign in with Redirect
          </button>
        </div>
      </div>
    </div>
  );
};

export interface AppProps {
  initialUser?: UserProfile | null;
  initialLoading?: boolean;
}

export const App: React.FC<AppProps> = ({
  initialUser = import.meta.env.MODE === "test" ? demoUser : null,
  initialLoading = import.meta.env.MODE === "test" ? false : true,
}) => {
  return (
    <AuthProvider initialUser={initialUser} initialLoading={initialLoading}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainAppLayout />} />
          <Route path="/login" element={<LoginView />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
